import { useEffect, useState } from "react";
import {
    Alert, Box, Button, Chip, CircularProgress, Divider, Input, Modal, ModalDialog,
    Option, Select, Sheet, Stack, Table, Typography,
} from "@mui/joy";

import { useAdmin } from "../../AdminContext";
import {
    APIAdminPayouts, APIAdminPayoutRequisites,
    APIAdminPayoutInProgress, APIAdminPayoutMarkPaid, APIAdminPayoutReject,
} from "../../api_admin_payouts";

const STATUS_COLORS = { requested: 'warning', in_progress: 'primary', paid: 'success', rejected: 'danger' };
const STATUS_LABELS = { requested: 'Запрошена', in_progress: 'В обработке', paid: 'Выплачена', rejected: 'Отклонена' };
const METHOD_LABELS = { card: 'Карта', sbp: 'СБП', bank_account: 'Расчётный счёт', manual: 'Ручной перевод' };


function RequisitesModal({ open, onClose, requisites, loading }) {
    return (
        <Modal open={open} onClose={onClose}>
            <ModalDialog sx={{ minWidth: 360 }}>
                <Typography level="title-lg">Реквизиты для перевода</Typography>
                {loading ? (
                    <Stack alignItems="center" sx={{ py: 3 }}><CircularProgress /></Stack>
                ) : requisites ? (
                    <Stack spacing={0.75} sx={{ mt: 1 }}>
                        {requisites.entity_type === 'company' ? (
                            <>
                                <Row k="Юр. название" v={requisites.legal_name} />
                                <Row k="Расчётный счёт" v={requisites.account_number} mono />
                                <Row k="БИК" v={requisites.bik} mono />
                                <Row k="Корр. счёт" v={requisites.corr_account} mono />
                                <Row k="ИНН" v={requisites.inn} mono />
                                <Row k="КПП" v={requisites.kpp} mono />
                            </>
                        ) : (
                            <>
                                <Row k="Держатель" v={requisites.card_holder_name} />
                                <Row k="Карта" v={requisites.card_number} mono />
                            </>
                        )}
                        <Row k="Банк" v={requisites.bank_name} />
                        <Alert color="warning" variant="soft" sx={{ mt: 1 }}>
                            Показ реквизитов зафиксирован в журнале аудита.
                        </Alert>
                    </Stack>
                ) : (
                    <Typography level="body-sm" color="neutral" sx={{ mt: 1 }}>Реквизиты не заполнены</Typography>
                )}
                <Button variant="outlined" color="neutral" onClick={onClose} sx={{ mt: 2 }}>Закрыть</Button>
            </ModalDialog>
        </Modal>
    );
}

function Row({ k, v, mono }) {
    return (
        <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Typography level="body-sm" color="neutral">{k}</Typography>
            <Typography level="body-sm" sx={mono ? { fontFamily: 'monospace' } : {}}>{v || '—'}</Typography>
        </Stack>
    );
}


export default function AdminPayoutsPage() {
    const { adminToken: token } = useAdmin();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('');
    const [msg, setMsg] = useState(null);

    const [reqModal, setReqModal] = useState({ open: false, loading: false, data: null });

    const load = async () => {
        try {
            setLoading(true);
            const r = await APIAdminPayouts(token, { status: statusFilter, page: 1 });
            setItems(r?.items || []);
        } catch (e) {
            setMsg({ type: 'danger', text: 'Не удалось загрузить выплаты' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { if (token) load(); }, [token, statusFilter]);

    const showRequisites = async (id) => {
        setReqModal({ open: true, loading: true, data: null });
        try {
            const r = await APIAdminPayoutRequisites(token, id);
            setReqModal({ open: true, loading: false, data: r?.requisites || null });
        } catch (e) {
            setReqModal({ open: false, loading: false, data: null });
            setMsg({ type: 'danger', text: e?.response?.data?.error || 'Нет доступа к реквизитам (нужна роль finance/superadmin)' });
        }
    };

    const act = async (fn, id, ...args) => {
        setMsg(null);
        try { await fn(token, id, ...args); await load(); }
        catch (e) { setMsg({ type: 'danger', text: e?.response?.data?.error || 'Ошибка операции' }); }
    };

    const reject = async (id) => {
        const comment = window.prompt('Причина отклонения:');
        if (comment) act(APIAdminPayoutReject, id, comment);
    };

    if (loading) return <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress /></Stack>;

    return (
        <Stack spacing={2}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography level="h3">Выплаты партнёрам</Typography>
                <Select placeholder="Все статусы" value={statusFilter}
                        onChange={(_, v) => setStatusFilter(v || '')} sx={{ minWidth: 180 }}>
                    <Option value="">Все статусы</Option>
                    <Option value="requested">Запрошены</Option>
                    <Option value="in_progress">В обработке</Option>
                    <Option value="paid">Выплачены</Option>
                    <Option value="rejected">Отклонены</Option>
                </Select>
            </Stack>

            {msg && <Alert color={msg.type}>{msg.text}</Alert>}

            <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
                <Table hoverRow>
                    <thead>
                        <tr>
                            <th>#</th><th>Партнёр</th><th>Сумма</th><th>Способ</th>
                            <th>Статус</th><th>Запрошена</th><th style={{ width: 320 }}>Действия</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 && (
                            <tr><td colSpan={7}><Typography level="body-sm" color="neutral" sx={{ py: 2 }}>Выплат нет</Typography></td></tr>
                        )}
                        {items.map(p => (
                            <tr key={p.id}>
                                <td>{p.id}</td>
                                <td>{p.partner_name}</td>
                                <td>{Number(p.amount).toLocaleString('ru-RU')} ₽</td>
                                <td>{METHOD_LABELS[p.method] || p.method}</td>
                                <td><Chip size="sm" color={STATUS_COLORS[p.status] || 'neutral'} variant="soft">
                                    {STATUS_LABELS[p.status] || p.status}</Chip></td>
                                <td>{new Date(p.requested_at).toLocaleString('ru-RU')}</td>
                                <td>
                                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                        <Button size="sm" variant="soft" onClick={() => showRequisites(p.id)}>Реквизиты</Button>
                                        {p.status === 'requested' && (
                                            <Button size="sm" variant="outlined"
                                                    onClick={() => act(APIAdminPayoutInProgress, p.id)}>В работу</Button>
                                        )}
                                        {(p.status === 'requested' || p.status === 'in_progress') && (
                                            <>
                                                <Button size="sm" color="success"
                                                        onClick={() => act(APIAdminPayoutMarkPaid, p.id, '')}>Выплачено</Button>
                                                <Button size="sm" color="danger" variant="outlined"
                                                        onClick={() => reject(p.id)}>Отклонить</Button>
                                            </>
                                        )}
                                    </Stack>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </Sheet>

            <RequisitesModal
                open={reqModal.open}
                loading={reqModal.loading}
                requisites={reqModal.data}
                onClose={() => setReqModal({ open: false, loading: false, data: null })}
            />
        </Stack>
    );
}